import {SplitBookingBundle} from '@findhotel/sapi'
import {SearchOffer} from 'modules/sapiSearch/slice'
import {stringify} from 'qs'
import {pickBy} from 'ramda'
import {v4 as uuidv4} from 'uuid'

import {getQueryStringObject} from '@vio/core/src/_web/utils/url'
import {getOfferPerks} from '@vio/core/src/offer/business/getOfferPerks'
import {Offer} from '@vio/core/src/offer/types/offer'
import {SplitBooking} from '@vio/core/src/room/types/room'

import {RoomOfferUrlError} from './errors'

import type {BaseUrlCheckoutParams} from '@vio/core/src/utils/url/types/UrlParams'

export const getClickedBundleId = (): string => {
  // TODO: improve this in sc-165477
  if (typeof window === 'undefined') return null
  const search = new URLSearchParams(location.search)
  return search.get('clickedBundle')
}

export const getClickedOfferId = (): string => {
  // TODO: improve this in sc-165477
  if (typeof window === 'undefined') return null
  const search = new URLSearchParams(location.search)
  return search.get('clickedOffer')
}

export const isVioOfferOrBundleSelected = (): boolean => {
  return Boolean(getClickedOfferId() || getClickedBundleId())
}

type RoomsSearchParams = Partial<{
  bundleId: string
  clickedOfferBaseRate: number
  clickedOfferId: string
  clickedOfferTaxes: number
  clickedOfferHotelFees: number
  providerCode: string
}>

export const getRoomsSearchParamsFromApUrl = (): RoomsSearchParams => {
  // TODO: improve this in sc-165477
  if (typeof window === 'undefined') return {}

  const search = new URLSearchParams(location.search)

  const bundleId = search.get('clickedBundle')
  const clickedOfferId = search.get('clickedOffer')
  const providerCode = search.get('clickedProvider')
  const clickedBase = search.get('clickedBase')
  const clickedFees = search.get('clickedFees')
  const clickedTaxes = search.get('clickedTaxes')

  const clickedOfferBaseRate =
    clickedBase !== null ? Number(search.get('clickedBase')) : null
  const clickedOfferHotelFees =
    clickedFees !== null ? Number(search.get('clickedFees')) : null
  const clickedOfferTaxes =
    clickedTaxes !== null ? Number(search.get('clickedTaxes')) : null

  const clickedOffer = pickBy(prop => prop !== null, {
    bundleId,
    clickedOfferBaseRate,
    clickedOfferId,
    clickedOfferTaxes,
    clickedOfferHotelFees,
    providerCode
  })
  return bundleId || clickedOfferId ? clickedOffer : undefined
}

/**
 * Getting redirectId either from URL params or generating a new one
 */
export const getApLandingRedirectId = (): string => {
  const urlRedirectId = getQueryStringObject()?.['redirectId']
  return typeof urlRedirectId === 'string' ? urlRedirectId : uuidv4()
}

interface GetCheckoutLinkParams {
  /**
   * The checkout URL received from the SAPI offer links
   */
  checkoutUrl: string
  /**
   * The params to be used in the checkout URL. SAPI rooms offer already have some params by default but we need to extend them with additional data
   */
  paymentPageParams: Partial<BaseUrlCheckoutParams>
}

/**
 *   Returns single url string composed of an object of params, used for both SB and regular room offers.
 *   Preserves existing query parameters from the base URL and extends them with additional parameters.
 * @param checkoutUrl - The checkout URL received from the SAPI offer links
 * @param paymentPageParams - additional params to be added to the checkout URL
 */
export const getCheckoutLink = ({
  checkoutUrl,
  paymentPageParams
}: GetCheckoutLinkParams): string => {
  try {
    const baseUrl = new URL(checkoutUrl)
    const existingParams = Object.fromEntries(baseUrl.searchParams.entries())

    // We only need to pass the first offer tk param if it exists. It's extracted from offers[0][tk]
    // untill we get it directly in the checkout link from SAPI/RAA (TBD)
    // To be removed here: https://app.shortcut.com/findhotel/story/160978
    const firstOfferTkParam = existingParams['offers[0][tk]']
      ? {firstOfferTk: existingParams['offers[0][tk]']}
      : {}

    const queryParams = stringify({
      ...firstOfferTkParam,
      ...existingParams,
      redirectId: paymentPageParams.redirectId,
      offerPerks: paymentPageParams.offerPerks,
      bookingHint: paymentPageParams.bookingHint
    })

    const finalCheckoutUrl = `${baseUrl.origin}${baseUrl.pathname}?${queryParams}`
    return finalCheckoutUrl
  } catch (error) {
    Sentry.captureException(
      new RoomOfferUrlError('Room offer URL is invalid', error),
      {extra: {checkoutUrl, paymentPageParams}}
    )
    return undefined
  }
}

export const extractSplitBookingParams = (
  splitBookingBundle: SplitBookingBundle
) => {
  if (!splitBookingBundle) return {}
  return {
    bundleId: splitBookingBundle.bundleId,
    clickedOfferBaseRate: splitBookingBundle.totalRate.base,
    clickedOfferTaxes: splitBookingBundle.totalRate.taxes,
    clickedOfferHotelFees: splitBookingBundle.totalRate.hotelFees,
    providerCode: splitBookingBundle?.offers[0].offer.providerCode
  }
}

export const extractRegularOfferParams = (offer: SearchOffer) => {
  if (!offer) return {}
  return {
    clickedOfferId: offer.id,
    clickedOfferBaseRate: offer.rate.base,
    clickedOfferTaxes: offer.rate.taxes,
    clickedOfferHotelFees: offer.rate.hotelFees,
    providerCode: offer.providerCode
  }
}

export type OfferCheckOfferParams = {
  offerId?: string
  providerCode: string
  providerRateType: string
  cug: string
  offerPerks?: string
}

export const roomOfferToOfferCheckParams = (
  offer: Offer
): OfferCheckOfferParams => {
  const offerPerks = getOfferPerks(
    offer?.cancellationPenalties,
    offer?.services
  )
  return {
    offerId: offer.id,
    providerCode: offer.providerCode,
    providerRateType: offer.providerRateType,
    cug: offer.cug[0],
    offerPerks
  }
}

export const splitBookingOfferToOfferCheckParams = (
  splitBooking: SplitBooking
): OfferCheckOfferParams => {
  return {
    providerCode: splitBooking.offers[0].offer.providerCode,
    providerRateType: splitBooking.offers[0].offer.providerRateType,
    cug: splitBooking.offers[0].offer.cug[0]
  }
}

export const getOfferCheckParams = ({
  offer,
  splitBooking
}: {
  offer?: Offer
  splitBooking?: SplitBooking
}) => {
  if (offer && splitBooking) {
    throw new Error('Offer and sb bundle cannot be present at the same time')
  }
  return offer
    ? roomOfferToOfferCheckParams(offer)
    : splitBookingOfferToOfferCheckParams(splitBooking)
}
